import React, { createRef, useMemo, useState } from 'react';

import { Form as StateForm, useFormState, ValidationRules } from '@neslotech/hooks';
import { generateId, isUndefined } from '@neslotech/utils';

import { toBase64 } from '../../../tools/file.util';

import { PerformanceReview } from '../../../interfaces/employee/performance-review.interface';
import { Employee } from '../../../interfaces/employee/employee.interface';
import { FileData } from '../../../interfaces/config/file.interface';

// import { ReactComponent as WordIcon } from '../../../icons/word-icon.svg';

import Form from '../../form/Form';
import FormRow from '../../form/form-row/FormRow';
import { OnChangeType } from '../../input/Input';
import { Datepicker, OnChangeType as DateChangeType } from '../../datepicker/Datepicker';
import FormAction from '../../form/form-action/FormAction';
import Select, { SelectItem, OnChangeType as SelectChangeType } from '../../select/Select';
import DetailsCard from '../../card/DetailsCard';
import { FileUpload } from '../../upload/FileUpload';
import Button from '../../button/Button';

import './performance-review-form.scss';
import DocumentIcon from '../../document-icon/DocumentIcon';

type OnAction = (payload: PerformanceReview, onComplete: () => void) => void;

interface Props {
  employees: Employee[];
  performanceReview?: PerformanceReview;
  onSave: OnAction;
}

export interface PerformanceReviewState extends StateForm {
  employeeId: string | number;
  startDate: string | Date;
  endDate: string | Date;
  document: FileData;
}

const rules: ValidationRules<PerformanceReviewState> = {
  validates: {
    employeeId: ['isPresent'],
    startDate: ['isPresent'],
    endDate: ['isPresent'],
    document: ['isPresent']
  }
};

const formify = (performanceReview?: PerformanceReview): PerformanceReviewState =>
  ({
    employeeId: performanceReview?.user_id,
    startDate: isUndefined(performanceReview?.start_date)
      ? undefined
      : new Date(performanceReview?.start_date),
    endDate: isUndefined(performanceReview?.end_date)
      ? undefined
      : new Date(performanceReview?.end_date),
    document: isUndefined(performanceReview?.document)
      ? undefined
      : {
          name: (performanceReview.document as FileData).name,
          url: (performanceReview.document as FileData).url,
          date: new Date((performanceReview.document as FileData)?.date),
          uploaded_by: (performanceReview.document as FileData)?.uploaded_by
        }
  }) as PerformanceReviewState;

const serverify = (form: PerformanceReviewState) => ({
  user_id: form.employeeId,
  start_date: form.startDate,
  end_date: form.endDate,
  document: form.document.data,
  file_name: form.document.name
});

const PerformanceReviewForm = ({ employees, performanceReview, onSave }: Props) => {
  const [form, setForm] = useFormState<PerformanceReviewState>(formify(performanceReview), rules);
  const handleChange = (
    newState: OnChangeType | DateChangeType | { document: FileData } | SelectChangeType
  ) => setForm({ ...form, ...(newState as PerformanceReviewState) });

  const [loading, setLoading] = useState<boolean>(false);

  const mappedEmployees = useMemo(() => {
    return employees
      .sort((a, b) => (a.full_name?.toLowerCase() > b.full_name?.toLowerCase() ? 1 : -1))
      .map<SelectItem>((employee: Employee) => ({
        label: employee.full_name,
        value: employee.id
      }));
  }, [employees]);

  const handleSubmit = (): void => {
    setLoading(true);

    const payload: PerformanceReview = serverify(form);
    onSave(payload, () => setLoading(false));
  };

  const handleFileChange = async (file: File) => {
    const base64Data = await toBase64(file);
    const fileData: FileData = {
      id: generateId(),
      name: file.name,
      uploaded_by: 'you',
      date: new Date(),
      data: base64Data
    };

    handleChange({ document: fileData });
  };

  const fileRef = createRef<HTMLInputElement>();

  return (
    <article className="performance-review-form">
      <DetailsCard>
        <Form>
          <FormRow>
            <Select
              name="employeeId"
              label="Employee"
              placeholder="Employee"
              value={form?.employeeId}
              error={form?.messages.employeeId}
              items={mappedEmployees}
              onChange={handleChange}
            />
          </FormRow>
          <FormRow>
            <Datepicker
              name="startDate"
              label="From Date"
              placeholder="From Date"
              value={form?.startDate}
              error={form?.messages.startDate}
              onChange={handleChange}
            />
          </FormRow>
          <FormRow>
            <Datepicker
              name="endDate"
              label="To Date"
              placeholder="To Date"
              value={form?.endDate}
              error={form?.messages.endDate}
              onChange={handleChange}
            />
          </FormRow>
          <FormRow>
            {!form?.document && (
              <FileUpload fileRef={fileRef} fileTypes="*/*" onChange={handleFileChange}>
                <Button label="Upload" hollow onClick={() => fileRef.current.click()} />
              </FileUpload>
            )}
            {!!form?.document && <UploadedFile file={form?.document} />}
          </FormRow>
          <FormAction
            loading={loading}
            label="Save Changes"
            onClick={handleSubmit}
            disabled={!form?.valid}
          />
        </Form>
      </DetailsCard>
    </article>
  );
};

interface FileProps {
  file: FileData;
}

const UploadedFile = ({ file }: FileProps) => (
  <article className="uploaded-file" onClick={() => window.open(file.url, '_blank')}>
    <section className="uploaded-file__name">
      <DocumentIcon file={file} />
      <p>{file.name}</p>
    </section>
    <small>
      Uploaded by {file.uploaded_by ?? 'you'} on {file.date.toLocaleDateString()}
    </small>
  </article>
);

export default PerformanceReviewForm;
